import { useEffect, useState } from "react";

// src/services/getPlaceDetails.ts
export interface PlaceDetails {
    formattedAddress: string;
    lat: number;
    lng: number;
    components: Record<string, string>;
}

export async function getPlaceDetails(placeId: string): Promise<PlaceDetails> {
    return new Promise((resolve, reject) => {
        const map = document.createElement("div"); // dummy div required by API
        const service = new google.maps.places.PlacesService(map);

        service.getDetails(
            { placeId, fields: ["address_components", "formatted_address", "geometry"] },
            (place, status) => {
                if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
                    reject(new Error(`Place details lookup failed: ${status}`));
                    return;
                }

                const components: Record<string, string> = {};
                if (place.address_components) {
                    for (const c of place.address_components) {
                        components[c.types[0]] = c.long_name;
                    }
                }

                resolve({
                    formattedAddress: place.formatted_address ?? "",
                    lat: place.geometry?.location?.lat() ?? 0,
                    lng: place.geometry?.location?.lng() ?? 0,
                    components,
                });
            }
        );
    });
}


// src/utils/loadGoogleMaps.ts
let googleMapsPromise: Promise<void> | null = null;

export function loadGoogleMaps(apiKey: string): Promise<void> {
    if (typeof window !== "undefined" && (window as any).google?.maps) {
        return Promise.resolve();
    }

    if (!googleMapsPromise) {
        googleMapsPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = reject;
            document.head.appendChild(script);
        });
    }

    return googleMapsPromise;
}

export interface AddressSuggestion {
    description: string;
    placeId: string;
}

interface UseAddressAutocompleteOptions {
    apiKey: string;
    debounceMs?: number;
}

export function useAddressAutocomplete(query: string, { apiKey, debounceMs = 300 }: UseAddressAutocompleteOptions) {
    const [results, setResults] = useState<AddressSuggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            return;
        }

        const controller = new AbortController();
        const delay = setTimeout(async () => {
            try {
                setLoading(true);
                await loadGoogleMaps(apiKey);
                const service = new google.maps.places.AutocompleteService();

                service.getPlacePredictions({ input: query }, (predictions, status) => {
                    if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
                        setResults([]);
                        return;
                    }

                    setResults(predictions.map((p) => ({
                        description: p.description,
                        placeId: p.place_id,
                    })));
                });
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }, debounceMs);

        return () => {
            clearTimeout(delay);
            controller.abort();
        };
    }, [query, apiKey, debounceMs]);

    return { results, loading, error };
}